import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { signOut } from "firebase/auth";

import { authService } from "../../../services/admin-auth/auth.service";
import { auth } from "../../../config/firebase";

const initialState = {
  isAuthenticated: false,
  user: null,
  phoneNumber: "",
  status: null,
  error: null,
  authMethod: null, // 'phone' | 'google'
  initializationStatus: null, // 'loading', 'fulfilled', 'rejected'
};

const sendOtp = createAsyncThunk(
  "authSlice/sendOtp",
  async (phoneData, { rejectWithValue }) => {
    try {
      const response = await authService.sendOtp(phoneData);
      return response;
    } catch (e) {
      const errorMessage =
        typeof e === "string"
          ? e
          : e.response?.data?.message || e.message || "Failed to send OTP";
      return rejectWithValue(errorMessage);
    }
  }
);

const verifyOtp = createAsyncThunk(
  "authSlice/verifyOtp",
  async (otpData, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOtp(otpData);
      return response;
    } catch (e) {
      const errorMessage =
        typeof e === "string"
          ? e
          : e.response?.data?.message || e.message || "Invalid OTP";
      return rejectWithValue(errorMessage);
    }
  }
);

const firebaseSignIn = createAsyncThunk(
  "authSlice/firebaseSignIn",
  async (firebaseData, { rejectWithValue }) => {
    try {
      const response = await authService.firebaseSignIn(firebaseData);
      return response;
    } catch (e) {
      const errorMessage =
        typeof e === "string"
          ? e
          : e.response?.data?.message || e.message || "Firebase sign-in failed";
      return rejectWithValue(errorMessage);
    }
  }
);

const getMe = createAsyncThunk(
  "authSlice/getMe",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getMe();
      return response;
    } catch (e) {
      // If getMe fails, it means the token is invalid or expired
      const errorMessage =
        typeof e === "string"
          ? e
          : e.response?.data?.message || e.message || "Authentication failed";
      return rejectWithValue(errorMessage);
    }
  }
);

const logout = createAsyncThunk(
  "authSlice/logout",
  async (_, { rejectWithValue }) => {
    try {
      // Backend logout is best-effort — Firebase-authed users have no JWT in UserTokens
      try {
        await authService.logout();
      } catch (backendErr) {
        console.warn("Backend logout skipped:", backendErr?.message);
      }

      await signOut(auth);
      return { message: "Logged out successfully" };
    } catch (e) {
      // Firebase sign-out failed — still clear local state
      try { await signOut(auth); } catch {}
      const errorMessage =
        typeof e === "string" ? e : e.message || "Failed to logout";
      return rejectWithValue(errorMessage);
    }
  }
);

const authSlice = createSlice({
  name: "authSlice",
  initialState,
  reducers: {
    setPhoneNumber: (state, action) => {
      state.phoneNumber = action.payload;
    },
    setAuthMethod: (state, action) => {
      state.authMethod = action.payload;
    },
    resetAuthState: (state) => {
      state.error = null;
      state.status = null;
      state.authMethod = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    initializeAuth: (state) => {
      // Check if user has auth state stored
      const isAuthenticated =
        localStorage.getItem("isAuthenticated") === "true";
      const phoneNumber = localStorage.getItem("phoneNumber");

      if (isAuthenticated && phoneNumber) {
        // Set basic auth state, but getMe will validate with server
        state.phoneNumber = phoneNumber;
        state.isAuthenticated = true;
        state.initializationStatus = "fulfilled";
        // Don't set isAuthenticated yet - wait for getMe validation
      }
    },
  },
  extraReducers: (builder) =>
    builder
      // Send OTP cases
      .addCase(sendOtp.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state, action) => {
        state.status = "fulfilled";
        state.otpSent = true;
        state.step = "otp";
        state.timer = 120; // 2 minutes
        state.error = null;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.status = "rejected";
        state.error = action.payload;
        state.otpSent = false;
      })
      // Verify OTP cases
      .addCase(verifyOtp.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.status = "fulfilled";
        state.isAuthenticated = true;
        state.otpVerified = true;

        // Handle user data - could be nested in user property or direct
        state.user = action.payload.user || action.payload;
        state.error = null;

        // Store in localStorage
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("phoneNumber", state.phoneNumber);
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.status = "rejected";
        state.error = action.payload;
        state.isAuthenticated = false;
        state.otpVerified = false;
      })
      // Firebase Sign In cases
      .addCase(firebaseSignIn.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(firebaseSignIn.fulfilled, (state, action) => {
        state.status = "fulfilled";
        state.isAuthenticated = true;

        // Handle user data - could be nested in user property or direct
        state.user = action.payload.user || action.payload;
        state.error = null;

        // Store in localStorage
        localStorage.setItem("isAuthenticated", "true");
        
        // Store phone number if available from user data
        if (state.user.phoneNumber) {
          state.phoneNumber = state.user.phoneNumber;
          localStorage.setItem("phoneNumber", state.user.phoneNumber);
        }
      })
      .addCase(firebaseSignIn.rejected, (state, action) => {
        state.status = "rejected";
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Get Me cases
      .addCase(getMe.pending, (state) => {
        state.initializationStatus = "loading";
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.initializationStatus = "fulfilled";
        state.isAuthenticated = true;
        state.user = action.payload; // Store the entire user object from API response
        state.error = null;

        // Store in localStorage to persist auth state
        localStorage.setItem("isAuthenticated", "true");

        // Update phone number from user data if it exists
        if (action.payload.phoneNumber) {
          state.phoneNumber = action.payload.phoneNumber;
          localStorage.setItem("phoneNumber", action.payload.phoneNumber);
        }
      })
      .addCase(getMe.rejected, (state, action) => {
        state.initializationStatus = "rejected";
        state.error = null; // Don't show error for failed revalidation

        // Check if Firebase still has a user
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) {
          // No Firebase user, clear everything
          state.isAuthenticated = false;
          state.user = null;
          localStorage.removeItem("isAuthenticated");
          localStorage.removeItem("phoneNumber");
        } else {
          // Firebase still has user, keep auth state but mark as needing validation
          // This allows the app to retry or show appropriate UI
          // Don't set isAuthenticated to false - keep it as is or check localStorage
          const storedAuth = localStorage.getItem("isAuthenticated") === "true";
          if (storedAuth) {
            // Keep the stored auth state if Firebase has user
            // This prevents redirect to login when backend is temporarily unavailable
            state.isAuthenticated = true;
          } else {
            state.isAuthenticated = false;
            state.user = null;
          }
        }
      })
      // Logout cases
      .addCase(logout.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(logout.fulfilled, (state, action) => {
        state.status = "fulfilled";
        state.isAuthenticated = false;
        state.user = null;
        state.phoneNumber = "";
        state.authMethod = null;
        state.error = null;
        state.status = null;
        state.initializationStatus = null;

        // Clear all auth data
        localStorage.removeItem("isAuthenticated");
        localStorage.removeItem("phoneNumber");
      })
      .addCase(logout.rejected, (state, action) => {
        state.status = "rejected";
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
      }),
});

const { reducer: authReducer, actions } = authSlice;
const authActions = {
  ...actions,
  sendOtp,
  verifyOtp,
  firebaseSignIn,
  getMe,
  logout,
};

export { authReducer, authActions };
